import React, { useEffect, useRef, useState } from "react";

export default function ChecklistQuestionEditor({
  question,
  isNew,
  sourceName,
  onSource,
  onCancel,
  onSave,
  onDelete,
}) {
  const [value, setValue] = useState(() => ({ ...question, required: false }));
  const input = useRef(null);
  useEffect(() => {
    input.current?.focus();
  }, []);

  const patch = (changes) =>
    setValue((current) => ({ ...current, ...changes }));
  const valid =
    value.question.trim() && value.referenceNo.trim() && value.section.trim();
  return (
    <aside
      className="checklist-question-editor"
      aria-label={isNew ? "Add question" : "Edit question"}
    >
      <header>
        <h2>
          {isNew ? "Add question" : "Edit question"}{" "}
          <small>{value.referenceNo}</small>
        </h2>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close question editor"
        >
          <i className="bx bx-x" />
        </button>
      </header>
      {!isNew && sourceName && (
        <button
          type="button"
          className="checklist-question-editor__source"
          onClick={onSource}
        >
          Source: {sourceName}
          {value.sourcePage ? ` - Page ${value.sourcePage}` : ""}{" "}
          <i className="bx bx-link-external" />
        </button>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (valid)
            onSave({
              ...value,
              question: value.question.trim(),
              referenceNo: value.referenceNo.trim(),
              section: value.section.trim(),
            });
        }}
      >
        <label>
          Question / requirement
          <textarea
            ref={input}
            rows={4}
            value={value.question}
            onChange={(event) =>
              patch({ question: event.target.value, needsCheck: true })
            }
            required
          />
        </label>
        <div className="checklist-question-editor__row">
          <label>
            Question ID
            <input
              value={value.referenceNo}
              onChange={(event) => patch({ referenceNo: event.target.value })}
              required
            />
          </label>
          <label>
            Section
            <input
              value={value.section}
              onChange={(event) => patch({ section: event.target.value })}
              required
            />
          </label>
        </div>
        <div className="checklist-question-editor__row">
          <label>
            Answer type
            <select
              value={value.answerType}
              onChange={(event) => patch({
                answerType: event.target.value,
                ...(event.target.value === "None" ? { required: false } : {}),
              })}
            >
              {[
                "None",
                "Yes / No / N/A",
                "Long text",
                "Short text",
                "Number",
                "Date",
              ].map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </label>
        </div>
        {[
          ["supporting", "Required information", "bx-file"],
        ].map(([field, title, icon]) => (
          <details key={field}>
            <summary>
              <i className={`bx ${icon}`} /> {title} (
              {
                value[field]
                  .split(field === "references" ? /[,\n]/ : "\n")
                  .filter((item) => item.trim()).length
              }
              )
            </summary>
            <label>
              <span className="visually-hidden">{title}</span>
              <textarea
                rows={3}
                value={value[field]}
                onChange={(event) => patch({ [field]: event.target.value })}
              />
            </label>
          </details>
        ))}
        <details>
          <summary>Explanation rule</summary>
          <label>
            <span className="visually-hidden">Explanation rule</span>
            <input
              value={value.explanation}
              placeholder="Require explanation when answer is No"
              onChange={(event) => patch({ explanation: event.target.value })}
            />
          </label>
        </details>
        {value.needsCheck && (
          <div className="checklist-question-editor__warning">
            <strong>
              <i className="bx bx-error" /> Confirm wording against source
            </strong>
            <p>
              Review the original source to ensure the question wording is
              accurate and complete.
            </p>
          </div>
        )}
        <label className="checklist-question-editor__reviewed">
          <input
            type="checkbox"
            checked={!value.needsCheck}
            onChange={(event) => patch({ needsCheck: !event.target.checked })}
          />{" "}
          Wording checked and reviewed
        </label>
        <footer>
          {onDelete && (
            <button type="button" className="danger" onClick={onDelete}>
              Delete
            </button>
          )}
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={!valid}>
            Save question
          </button>
        </footer>
      </form>
    </aside>
  );
}
